import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getCompactWindowItems, getSlidingWindowItems } from "../lib/adminPagination";

const VARIANTS = {
  admin: {
    bar: "flex flex-wrap items-center justify-center gap-1 sm:gap-2 min-h-10 max-w-full",
    nav: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded bg-white border border-gray-300 text-black disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-white",
    pageIdle: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded text-xs border bg-white text-black border-gray-300 hover:bg-gray-50",
    pageActive: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded text-xs border bg-black text-white border-black",
    ellipsis: "w-6 h-8 sm:w-9 sm:h-9 min-w-6 sm:min-w-9 shrink-0 inline-flex items-center justify-center text-xs text-gray-500 select-none pointer-events-none",
  },
  public: {
    bar: "flex flex-wrap items-center justify-center gap-1 sm:gap-2 min-h-10 max-w-full",
    nav: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded-md bg-[#0f0f0f] border border-red-700/60 text-white disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-[#0f0f0f]",
    pageIdle: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded-md text-xs border bg-[#0f0f0f] text-white border-red-700/60 hover:border-red-500",
    pageActive: "w-8 h-8 min-w-8 sm:w-9 sm:h-9 sm:min-w-9 shrink-0 rounded-md text-xs border bg-red-600 text-white border-red-600",
    ellipsis: "w-6 h-8 sm:w-9 sm:h-9 min-w-6 sm:min-w-9 shrink-0 inline-flex items-center justify-center text-xs text-gray-500 select-none pointer-events-none",
  },
};

const useCompactPager = () => {
  const [compact, setCompact] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : true
  ));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(max-width: 640px)");
    const sync = () => setCompact(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return compact;
};

const AdminPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
  variant = "admin",
}) => {
  const styles = VARIANTS[variant] || VARIANTS.admin;
  const compact = useCompactPager();
  const safeTotal = Math.max(1, Number(totalPages) || 1);
  const safeCurrent = Math.min(safeTotal, Math.max(1, Number(currentPage) || 1));
  const items = compact
    ? getCompactWindowItems(safeCurrent, safeTotal)
    : getSlidingWindowItems(safeCurrent, safeTotal);
  const atFirst = safeCurrent === 1;
  const atLast = safeCurrent === safeTotal;

  return (
    <nav className={`${styles.bar} ${className}`.trim()} aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, safeCurrent - 1))}
        disabled={atFirst}
        className={styles.nav}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} className="mx-auto" />
      </button>

      {items.map((item, index) => {
        if (item.type === "ellipsis") {
          return (
            <span key={`ellipsis-${index}`} className={styles.ellipsis} aria-hidden="true">
              ...
            </span>
          );
        }

        const isActive = item.page === safeCurrent;
        return (
          <button
            key={item.page}
            type="button"
            onClick={() => onPageChange(item.page)}
            className={isActive ? styles.pageActive : styles.pageIdle}
            aria-current={isActive ? "page" : undefined}
          >
            {item.page}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(safeTotal, safeCurrent + 1))}
        disabled={atLast}
        className={styles.nav}
        aria-label="Next page"
      >
        <ChevronRight size={14} className="mx-auto" />
      </button>
    </nav>
  );
};

export default AdminPagination;
